import { useState, useEffect, useRef } from 'react';
import { getStoredUserId } from '../utils/analytics';
import { WS_BASE_URL } from '../utils/apiConfig';

function getWsUrl() {
  if (typeof window === 'undefined') return null;
  const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsHost = `localhost:${window.location.port}`;
  const baseUrl = isDev
    ? (WS_BASE_URL ? `${WS_BASE_URL}/news` : `${wsProtocol}//${wsHost}/news`)
    : `${WS_BASE_URL || `${wsProtocol}//${window.location.host}`}/news`;
  const userId = getStoredUserId();
  return `${baseUrl}?userId=${encodeURIComponent(userId)}`;
}

function formatRelativeTime(date) {
  const diffMs = Date.now() - new Date(date).getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  return new Date(date).toLocaleDateString();
}

function processNewsItem(item) {
  return {
    ...item,
    time: formatRelativeTime(item.pubDate),
    url: item.url?.startsWith('http') ? item.url : `https://${item.url}`
  };
}

const MAX_NEWS = 50;

export function useAllCoinNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const connect = () => {
      const wsUrl = getWsUrl();
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setIsConnected(true);
        ws.send(JSON.stringify({ type: 'subscribe_all_coin_news' }));
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'initial_all_coin_news') {
            const processed = (message.data || []).map(processNewsItem).slice(0, MAX_NEWS);
            setNews(processed);
            setLoading(false);
          } else if (message.type === 'coin_news' && message.data) {
            const item = processNewsItem(message.data);
            setNews(prev => {
              if (prev.some(n => n.guid === item.guid)) return prev;
              return [item, ...prev].slice(0, MAX_NEWS);
            });
          }
        } catch (err) {
          console.error('[useAllCoinNews] Error parsing message:', err);
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, 5000);
      };
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
    };
  }, []);

  // Update relative times every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNews(prev => prev.map(item => ({
        ...item,
        time: formatRelativeTime(item.pubDate)
      })));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return { news, loading, isConnected };
}

export default useAllCoinNews;
